import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    persisted: Boolean,
    initialStep: { type: String, default: "basics" },
    hasErrors: Boolean
  }

  static targets = [
    "navigation",
    "stepButton",
    "stepPanel",
    "previousButton",
    "nextButton",
    "finishButton",
    "stepCounter",
    "stepDescription",
    "audienceInput",
    "audienceCard",
    "businessAccessInput",
    "businessAccessCard",
    "businessSection",
    "consumerSection",
    "approvedClients",
    "name",
    "slug",
    "slugPreview",
    "brandAsset",
    "catalogSelect",
    "deliverySelect",
    "clientSelect",
    "consumerClient",
    "consumerPrice",
    "legalBody",
    "legalPublished",
    "catalogCount",
    "deliveryCount",
    "clientCount",
    "readinessCount",
    "readinessItem",
    "readinessReady",
    "readinessIncomplete"
  ]

  connect() {
    this.brandPreviewUrls = new Map()
    this.brandAssetTargets.forEach((asset) => this.updateBrandAssetPreview(asset))
    this.steps = this.stepButtonTargets.map((button) => button.dataset.step)
    this.currentStep = this.hasErrorsValue ? this.initialStepValue : (this.stepFromLocation() || this.initialStepValue)
    this.boundSelectionChanged = this.refresh.bind(this)
    this.selectionTargets().forEach((select) => {
      $(select).on("change.commerceStorefrontForm", this.boundSelectionChanged)
    })
    this.slugWasEdited = this.persistedValue || this.slugTarget.value.trim().length > 0
    this.updateSlugFromName()
    this.refresh()
  }

  disconnect() {
    this.resetBrandAssetPreviews()
    this.selectionTargets().forEach((select) => {
      $(select).off("change.commerceStorefrontForm", this.boundSelectionChanged)
    })
  }

  brandAssetChanged(event) {
    this.updateBrandAssetPreview(event.currentTarget.closest(".storefront-brand-asset"))
  }

  updateBrandAssetPreview(asset, { reset = false } = {}) {
    const previousUrl = this.brandPreviewUrls.get(asset)
    if (previousUrl) URL.revokeObjectURL(previousUrl)
    this.brandPreviewUrls.delete(asset)

    const file = reset ? null : asset.querySelector('input[type="file"]').files[0]
    const url = file ? URL.createObjectURL(file) : asset.dataset.savedPreviewUrl
    if (file) this.brandPreviewUrls.set(asset, url)

    const image = asset.querySelector("img")
    const empty = asset.querySelector("[data-brand-asset-empty]")
    if (url) image.src = url
    else image.removeAttribute("src")
    this.toggle(image, Boolean(url))
    this.toggle(empty, !url)
    empty.textContent = "No image uploaded."
  }

  brandAssetPreviewFailed(event) {
    const image = event.currentTarget
    const empty = image.closest(".storefront-brand-asset").querySelector("[data-brand-asset-empty]")
    this.toggle(image, false)
    this.toggle(empty, true)
    empty.textContent = "Preview unavailable. Please choose a supported image."
  }

  resetBrandAssetPreviews() {
    this.brandAssetTargets.forEach((asset) => this.updateBrandAssetPreview(asset, { reset: true }))
  }

  nameChanged() {
    this.updateSlugFromName()
    this.refresh()
  }

  slugChanged() {
    this.slugWasEdited = true
    this.updateSlugPreview()
    this.refresh()
  }

  audienceChanged() {
    this.refresh()
  }

  businessAccessChanged() {
    this.refresh()
  }

  refresh() {
    const audience = this.checkedValue(this.audienceInputTargets)
    const businessEnabled = ["business_only", "business_and_consumer"].includes(audience)
    const consumerEnabled = ["consumer_only", "business_and_consumer"].includes(audience)
    const approvedClients = this.checkedValue(this.businessAccessInputTargets) === "approved_clients"

    this.toggle(this.approvedClientsTarget, businessEnabled && approvedClients)
    this.highlightCards(this.audienceCardTargets, "border-primary", "bg-subtle-primary")
    this.highlightCards(this.businessAccessCardTargets, "border-info", "bg-subtle-info")
    this.renderStep()

    this.catalogCountTarget.textContent = this.selectionLabel(this.catalogSelectTarget, "catalog")
    this.deliveryCountTarget.textContent = this.selectionLabel(this.deliverySelectTarget, "method")
    this.clientCountTarget.textContent = this.selectionLabel(this.clientSelectTarget, "client")

    const requirements = {
      name: this.nameTarget.value.trim().length > 0 && this.slugTarget.value.trim().length > 0,
      catalog: this.catalogSelectTarget.selectedOptions.length > 0,
      delivery: this.deliverySelectTarget.selectedOptions.length > 0,
      consumer_price: this.consumerPriceTarget.value.length > 0,
      consumer_client: this.consumerClientTarget.value.length > 0,
      legal_terms: this.legalDocumentComplete("terms"),
      legal_privacy: this.legalDocumentComplete("privacy")
    }

    const applicableItems = this.readinessItemTargets.filter((item) => {
      const applicable = !item.hasAttribute("data-consumer-requirement") || consumerEnabled
      this.toggle(item, applicable)
      return applicable
    })

    let readyCount = 0
    applicableItems.forEach((item) => {
      const complete = requirements[item.dataset.requirement]
      readyCount += complete ? 1 : 0
      const icon = item.querySelector("[data-readiness-icon]")
      const label = item.querySelector("[data-readiness-label]")
      icon.innerHTML = complete
        ? '<i class="fa-solid fa-circle-check fg-success"></i>'
        : '<i class="fa-regular fa-circle fg-3"></i>'
      label.classList.toggle("fg-2", !complete)
    })
    this.readinessCountTarget.textContent = `${readyCount}/${applicableItems.length}`
    const ready = readyCount === applicableItems.length
    this.toggle(this.readinessReadyTarget, ready)
    this.toggle(this.readinessIncompleteTarget, !ready)
  }

  selectStep(event) {
    this.showStep(event.currentTarget.dataset.step, { updateLocation: true })
  }

  previousStep() {
    const index = this.steps.indexOf(this.currentStep)
    if (index > 0) this.showStep(this.steps[index - 1], { updateLocation: true, scroll: true })
  }

  nextStep() {
    const index = this.steps.indexOf(this.currentStep)
    if (index < this.steps.length - 1) this.showStep(this.steps[index + 1], { updateLocation: true, scroll: true })
  }

  validate(event) {
    const invalidControl = Array.from(this.element.elements).find((control) => control.willValidate && !control.validity.valid)
    if (!invalidControl) return

    event.preventDefault()
    event.stopImmediatePropagation()
    const panel = invalidControl.closest("[data-commerce-storefront-form-target~='stepPanel']")
    if (panel) this.showStep(panel.dataset.step, { updateLocation: true, scroll: true })
    window.requestAnimationFrame(() => invalidControl.reportValidity())
  }

  showStep(step, { updateLocation = false, scroll = false } = {}) {
    if (!this.steps.includes(step)) return

    this.currentStep = step
    this.renderStep()
    if (updateLocation) window.history.replaceState({}, "", `#storefront-step-${step}`)
    if (scroll) this.navigationTarget.scrollIntoView({ block: "start", behavior: "smooth" })
  }

  renderStep() {
    const businessEnabled = ["business_only", "business_and_consumer"].includes(this.checkedValue(this.audienceInputTargets))
    const consumerEnabled = ["consumer_only", "business_and_consumer"].includes(this.checkedValue(this.audienceInputTargets))

    this.stepPanelTargets.forEach((panel) => {
      let visible = panel.dataset.step === this.currentStep
      if (panel === this.businessSectionTarget) visible = visible && businessEnabled
      if (panel === this.consumerSectionTarget) visible = visible && consumerEnabled
      this.toggle(panel, visible)
    })

    this.stepButtonTargets.forEach((button) => {
      const active = button.dataset.step === this.currentStep
      button.classList.toggle("active", active)
      button.setAttribute("aria-current", active ? "step" : "false")
    })

    const index = this.steps.indexOf(this.currentStep)
    const activeButton = this.stepButtonTargets[index]
    this.previousButtonTarget.disabled = index === 0
    this.toggle(this.nextButtonTarget, index !== this.steps.length - 1)
    this.toggle(this.finishButtonTarget, index === this.steps.length - 1)
    this.stepCounterTarget.textContent = `Step ${index + 1} of ${this.steps.length}`
    this.stepDescriptionTarget.textContent = activeButton?.dataset.description || ""
  }

  stepFromLocation() {
    const hash = window.location.hash
    if (!hash) return null

    const directStep = hash.replace("#storefront-step-", "")
    if (this.steps.includes(directStep)) return directStep

    return this.stepPanelTargets.find((panel) => `#${panel.id}` === hash)?.dataset.step || null
  }

  checkedValue(inputs) {
    return inputs.find((input) => input.checked)?.value
  }

  toggle(element, visible) {
    element.classList.toggle("d-none", !visible)
  }

  selectionLabel(select, noun) {
    const count = select.selectedOptions.length
    return `${count} ${count === 1 ? noun : `${noun}s`} selected`
  }

  highlightCards(cards, borderClass, backgroundClass) {
    cards.forEach((card) => {
      const selected = card.querySelector("input")?.checked
      card.classList.toggle(borderClass, selected)
      card.classList.toggle(backgroundClass, selected)
    })
  }

  selectionTargets() {
    return [
      this.catalogSelectTarget,
      this.deliverySelectTarget,
      this.clientSelectTarget,
      this.consumerClientTarget,
      this.consumerPriceTarget
    ]
  }

  legalDocumentComplete(kind) {
    const body = this.legalBodyTargets.find((input) => input.dataset.legalKind === kind)
    const published = this.legalPublishedTargets.find((input) => input.dataset.legalKind === kind)
    return Boolean(body?.value.trim().length && published?.checked)
  }

  updateSlugFromName() {
    if (!this.slugWasEdited) {
      this.slugTarget.value = this.nameTarget.value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
    }
    this.updateSlugPreview()
  }

  updateSlugPreview() {
    this.slugPreviewTarget.textContent = this.slugTarget.value.trim() || "your-store"
  }
}
